import type { CSSProperties, ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { useReveal } from './useReveal'

type DrawMode = 'load' | 'scroll'

const ARROWS = {
  'curve-down-left': {
    viewBox: '0 0 100 72',
    body: 'M92 6 C 66 8, 34 22, 18 60',
    head: 'M18 60 L 13 44 M18 60 L 33 53',
  },
  'curve-down-right': {
    viewBox: '0 0 100 72',
    body: 'M8 6 C 34 8, 66 22, 82 60',
    head: 'M82 60 L 67 53 M82 60 L 87 44',
  },
  'swoosh-right': {
    viewBox: '0 0 140 60',
    body: 'M6 44 C 30 8, 74 6, 98 30 S 124 50, 132 22',
    head: 'M132 22 L 118 26 M132 22 L 134 37',
  },
  'curve-up-right': {
    viewBox: '0 0 100 72',
    body: 'M8 64 C 30 60, 62 46, 84 12',
    head: 'M84 12 L 70 18 M84 12 L 86 28',
  },
  'curve-left': {
    viewBox: '0 0 100 56',
    body: 'M94 40 C 72 10, 36 6, 10 30',
    head: 'M10 30 L 25 31 M10 30 L 17 16',
  },
  'loop-down-right': {
    viewBox: '0 0 100 80',
    body: 'M8 10 C 36 2, 58 26, 42 40 C 28 52, 26 26, 52 30 C 74 34, 84 52, 86 70',
    head: 'M86 70 L 74 62 M86 70 L 92 56',
  },
} as const

export type HandArrowVariant = keyof typeof ARROWS

function drawStyle(delay: number) {
  return { '--draw-delay': `${delay}ms`, '--reveal-delay': `${delay}ms` } as CSSProperties
}

export function HandArrow({
  variant,
  draw = 'scroll',
  delay = 0,
  className,
}: {
  variant: HandArrowVariant
  draw?: DrawMode
  delay?: number
  className?: string
}) {
  const ref = useReveal<SVGSVGElement>('draw')
  const arrow = ARROWS[variant]

  return (
    <svg
      ref={draw === 'scroll' ? ref : undefined}
      aria-hidden="true"
      focusable="false"
      viewBox={arrow.viewBox}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn(draw === 'load' && 'animate-draw', className)}
      style={drawStyle(delay)}
    >
      <path className="hand-stroke" pathLength={1} d={arrow.body} />
      <path className="hand-stroke" pathLength={1} d={arrow.head} />
    </svg>
  )
}

export function HandNote({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <p
      className={cn(
        'text-hand text-[22px] leading-[1.15] text-[var(--marker)] sm:text-2xl',
        className,
      )}
    >
      {children}
    </p>
  )
}

export function HandUnderline({
  draw = 'scroll',
  delay = 0,
  className,
  children,
}: {
  draw?: DrawMode
  delay?: number
  className?: string
  children: ReactNode
}) {
  const ref = useReveal<HTMLSpanElement>('draw')

  return (
    <span
      ref={draw === 'scroll' ? ref : undefined}
      className={cn('relative inline-block whitespace-nowrap', draw === 'load' && 'animate-draw')}
      style={drawStyle(delay)}
    >
      {children}
      <svg
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 120 12"
        preserveAspectRatio="none"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.6}
        strokeLinecap="round"
        className={cn(
          'pointer-events-none absolute -bottom-2 left-0 h-3 w-full text-[var(--marker)]',
          className,
        )}
      >
        <path className="hand-stroke" pathLength={1} d="M3 8 C 32 3, 70 2, 117 6" />
      </svg>
    </span>
  )
}

export function HandCircle({
  draw = 'scroll',
  delay = 0,
  className,
  children,
}: {
  draw?: DrawMode
  delay?: number
  className?: string
  children: ReactNode
}) {
  const ref = useReveal<HTMLSpanElement>('draw')

  return (
    <span
      ref={draw === 'scroll' ? ref : undefined}
      className={cn(
        'relative inline-block whitespace-nowrap px-1',
        draw === 'load' && 'animate-draw',
      )}
      style={drawStyle(delay)}
    >
      {children}
      <svg
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 120 50"
        preserveAspectRatio="none"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.2}
        strokeLinecap="round"
        className={cn(
          'pointer-events-none absolute -inset-x-2 -inset-y-2 h-[calc(100%+16px)] w-[calc(100%+16px)] text-[var(--marker)]',
          className,
        )}
      >
        <path
          className="hand-stroke"
          pathLength={1}
          d="M62 4 C 24 2, 4 14, 6 27 C 8 42, 44 48, 78 45 C 108 42, 118 30, 112 18 C 106 7, 80 3, 50 6"
        />
      </svg>
    </span>
  )
}
